import { AdminPanelSettings as AdminPanelSettingsIcon } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';
import React from 'react';
import { getModelIcon, getModelLabel } from '../../utils/ModelIconMapping';
import { Palette } from 'Palette';
import { StyledTypography } from '../styles/styles';
import ActivityLogModelCard from './ActivityLogModelCard';
import { getActivityLogDisplayName } from './activityLogPageEntryHelpers';

interface ActivityLogPageEntryLeftPanelProps {
  log: any;
  tone: keyof typeof Palette;
  contactNames: Map<string, string>;
  isEmailEvent: boolean;
}

const ActivityLogPageEntryLeftPanel: React.FC<ActivityLogPageEntryLeftPanelProps> = ({
  log,
  tone,
  contactNames,
  isEmailEvent,
}) => {
  const getDisplayName = (description: any, modelType?: string, externalId?: string) =>
    getActivityLogDisplayName(description, contactNames, modelType, externalId);

  const LinkedModelsList = () => {
    const modelsToShow = log.linkedModelDescriptions || log.linkedModels;
    if (!modelsToShow?.length) return null;

    return (
      <Box sx={{ mt: 1 }}>
        <StyledTypography
          tone={tone}
          fontSize="0.75rem"
          sx={{ mb: 0.5, display: 'block', opacity: 0.7 }}
        >
          Related:
        </StyledTypography>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {modelsToShow.map((link: any, index: number) => {
            const description = 'description' in link ? (link as any).description : null;
            const displayText =
              description?.name || description?.contactName || link.linkedModelExternalId;

            return (
              <ActivityLogModelCard
                key={index}
                title={displayText}
                subtitle={getModelLabel(link.linkedModelType)}
                icon={getModelIcon(link.linkedModelType)}
                tone={tone}
                modelType={link.linkedModelType}
                modelId={link.linkedModelExternalId}
                maxWidth="180px"
              />
            );
          })}
        </Stack>
      </Box>
    );
  };

  const AssociatedModelDescription = () => {
    if (!log.associatedModelDescription) return null;

    const displayText = getDisplayName(
      log.associatedModelDescription,
      log.associatedModelType,
      log.associatedModelExternalId,
    );

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <StyledTypography
          tone={tone}
          fontSize="0.875rem"
          sx={{ mr: 1, minWidth: '60px', opacity: 0.7 }}
        >
          <strong>Target:</strong>
        </StyledTypography>
        <ActivityLogModelCard
          title={displayText}
          subtitle={getModelLabel(log.associatedModelType)}
          icon={getModelIcon(log.associatedModelType)}
          tone={tone}
          modelType={log.associatedModelType}
          modelId={log.associatedModelExternalId}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <StyledTypography
          tone={tone}
          fontSize="0.875rem"
          sx={{ mr: 1, minWidth: '60px', opacity: 0.7 }}
        >
          <strong>Initiator:</strong>
        </StyledTypography>
        <ActivityLogModelCard
          title={getDisplayName(
            log.initiatedByDescription,
            log.initiatedByType,
            log.initiatedByExternalId,
          )}
          subtitle={getModelLabel(log.initiatedByType)}
          icon={
            (log.advisingForExternalId && log.advisingForType) ||
            log.initiatedByDescription?.isAdvisor ? (
              <AdminPanelSettingsIcon />
            ) : (
              getModelIcon(log.initiatedByType)
            )
          }
          tone={tone}
          modelType={log.initiatedByType}
          modelId={log.initiatedByExternalId}
        />
      </Box>
      {(log.advisingForExternalId && log.advisingForType) ||
      log.initiatedByDescription?.isAdvisor ? (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <StyledTypography
            tone={tone}
            fontSize="0.875rem"
            sx={{ mr: 1, minWidth: '60px', opacity: 0.7 }}
          >
            <strong>Advising:</strong>
          </StyledTypography>
          <ActivityLogModelCard
            title={getDisplayName(
              { name: log.initiatedByDescription?.advisingForName },
              log.advisingForType || log.initiatedByDescription?.advisingForType || 'Unknown',
              log.advisingForExternalId || log.initiatedByDescription?.advisingForExternalId || '',
            )}
            subtitle={getModelLabel(
              log.advisingForType || log.initiatedByDescription?.advisingForType || 'Unknown',
            )}
            icon={getModelIcon(
              log.advisingForType || log.initiatedByDescription?.advisingForType || 'Unknown',
            )}
            tone={tone}
            modelType={
              log.advisingForType || log.initiatedByDescription?.advisingForType || 'Unknown'
            }
            modelId={
              log.advisingForExternalId || log.initiatedByDescription?.advisingForExternalId || ''
            }
          />
        </Box>
      ) : null}
      {(log.onBehalfOfType && log.onBehalfOfExternalId) ||
      log.initiatedByDescription?.isImpersonated ? (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <StyledTypography
            tone={tone}
            fontSize="0.875rem"
            sx={{ mr: 1, minWidth: '60px', opacity: 0.7 }}
          >
            <strong>Impersonating:</strong>
          </StyledTypography>
          <ActivityLogModelCard
            title={getDisplayName(
              { name: log.initiatedByDescription?.onBehalfOfName },
              log.onBehalfOfType || log.initiatedByDescription?.onBehalfOfType || 'Unknown',
              log.onBehalfOfExternalId || log.initiatedByDescription?.onBehalfOfExternalId || '',
            )}
            subtitle={getModelLabel(
              log.onBehalfOfType || log.initiatedByDescription?.onBehalfOfType || 'Unknown',
            )}
            icon={getModelIcon(
              log.onBehalfOfType || log.initiatedByDescription?.onBehalfOfType || 'Unknown',
            )}
            tone={tone}
            modelType={
              log.onBehalfOfType || log.initiatedByDescription?.onBehalfOfType || 'Unknown'
            }
            modelId={
              log.onBehalfOfExternalId || log.initiatedByDescription?.onBehalfOfExternalId || ''
            }
          />
        </Box>
      ) : null}
      <AssociatedModelDescription />
      {!log.associatedModelDescription && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <StyledTypography
            tone={tone}
            fontSize="0.875rem"
            sx={{ mr: 1, minWidth: '60px', opacity: 0.7 }}
          >
            <strong>Target:</strong>
          </StyledTypography>
          <ActivityLogModelCard
            title={getDisplayName(null, log.associatedModelType, log.associatedModelExternalId)}
            subtitle={getModelLabel(log.associatedModelType)}
            icon={getModelIcon(log.associatedModelType)}
            tone={tone}
            modelType={log.associatedModelType}
            modelId={log.associatedModelExternalId}
          />
        </Box>
      )}
      {isEmailEvent && log.associatedModelType === 'CONTACT' && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <StyledTypography
            tone={tone}
            fontSize="0.875rem"
            sx={{ mr: 1, minWidth: '60px', opacity: 0.7 }}
          >
            <strong>Contact:</strong>
          </StyledTypography>
          <ActivityLogModelCard
            title={getDisplayName(
              log.associatedModelDescription,
              'CONTACT',
              log.associatedModelExternalId,
            )}
            subtitle={getModelLabel('CONTACT')}
            icon={getModelIcon('CONTACT')}
            tone={tone}
            modelType="CONTACT"
            modelId={log.associatedModelExternalId}
          />
        </Box>
      )}
      <LinkedModelsList />
    </Box>
  );
};

export default ActivityLogPageEntryLeftPanel;
